import { useEffect, useRef, useState } from "react";

export function useWebsocket(url, {
  onMessage,
  onOpen,
  onClose,
  onError,
  maxRetries = 5,
  retryDelay = 3000,
  enabled = true,
} = {}) {
  const socketRef = useRef(null);
  const reconnectTimer = useRef(null);
  const retriesRef = useRef(0);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!enabled || !url) return;
    let manualClose = false;

    const connect = () => {
      const socket = new WebSocket(url);
      socketRef.current = socket;

      socket.onopen = (event) => {
        clearTimeout(reconnectTimer.current);
        retriesRef.current = 0;
        setIsConnected(true);
        onOpen?.(event);
      };

      socket.onmessage = (event) => onMessage?.(event);

      socket.onclose = (event) => {
        setIsConnected(false);
        onClose?.(event);
        if (!manualClose && retriesRef.current < maxRetries) {
          retriesRef.current += 1;
          const delay = retryDelay * retriesRef.current;
          reconnectTimer.current = setTimeout(connect, delay);
        } else if (retriesRef.current >= maxRetries) {
          console.warn("Max reconnect attempts reached, giving up."); // TODO Show correct notification
        }
      };

      socket.onerror = (err) => {
        onError?.(err);
        socket.close();
      };
    };

    connect();

    return () => {
      manualClose = true;
      clearTimeout(reconnectTimer.current);
      socketRef.current?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, enabled]);

  const send = (data) => {
    const ws = socketRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(typeof data === "string" ? data : JSON.stringify(data));
    }
  };

  return { send, isConnected };
}
